import { Module, MutationTree, ActionTree, GetterTree } from 'vuex'
import {
  ValuationEditState,
  ValuationEdit,
  ValuationEditObject,
} from '@/types/store/valuation'
import { RootState } from '@/types/store'
import client from '@/services/api/client'
import createMessage from '@/services/app/createMessage'
import { MessageType } from '@/interfaces/app/Message'
import { getNestedValue } from '@/util/helpers'
import {
  BuildingType,
  BuildingStatus,
} from '@/interfaces/valuation/types/BuildingValuationStep'
import download from 'downloadjs'
import router from '@/router'

const mutations: MutationTree<ValuationEditState> = {
  setData(state, payload: any) {
    state.data = payload
  },
  setEditObject(state, payload: ValuationEditObject) {
    state.edits = payload
  },
  addEdit(
    state,
    { categoryKey, edit }: { categoryKey: string; edit: ValuationEdit }
  ) {
    if (!state.edits[categoryKey]) {
      state.edits[categoryKey] = {}
    }
    state.edits = {
      ...state.edits,
      [categoryKey]: {
        ...state.edits[categoryKey],
        ...edit,
      },
    }
  },
  addProjectGroupsEdit(
    state,
    { categoryKey, edit }: { categoryKey: string; edit: ValuationEdit }
  ) {
    if (!state.edits.projectGroups) {
      state.edits.projectGroups = {}
    }
    if (!state.edits.projectGroups[categoryKey]) {
      state.edits.projectGroups[categoryKey] = {}
    }

    state.edits = {
      ...state.edits,
      projectGroups: {
        ...state.edits.projectGroups,
        [categoryKey]: {
          // @ts-ignore
          ...state.edits.projectGroups[categoryKey],
          ...edit,
        },
      },
    }
  },
  addCoefficientEdit(
    state,
    { categoryKey, edit }: { categoryKey: string; edit: ValuationEdit }
  ) {
    if (!state.edits.transactionCoefficients) {
      state.edits.transactionCoefficients = {}
    }
    if (!state.edits.transactionCoefficients[categoryKey]) {
      state.edits.transactionCoefficients[categoryKey] = {}
    }

    state.edits = {
      ...state.edits,
      transactionCoefficients: {
        ...state.edits.transactionCoefficients,
        [categoryKey]: {
          // @ts-ignore
          ...state.edits.transactionCoefficients[categoryKey],
          ...edit,
        },
      },
    }
  },
  setAdvertisingData(state, payload: any) {
    state.advertisingData = payload
  },
  resetAdvertisingData(state) {
    state.advertisingData = {}
  },
  setOffersData(state, payload: any) {
    state.offersData = payload
  },
  resetOffersData(state) {
    state.offersData = {}
  },
  resetValuationData(state) {
    state.data = {}
    state.edits = {}
  },
}

const actions: ActionTree<ValuationEditState, RootState> = {
  async fetchValuation({ commit, dispatch }, valuationId: string) {
    commit('app/setLoading', true, { root: true })
    return client({
      url: `/v1/valuation/session/${valuationId}`,
      method: 'GET',
    })
      .then(response => {
        commit('setData', response.data)
      })
      .catch(err => {
        const message = createMessage(
          'Valuace již není dostupná k úpravě.',
          MessageType.Error
        )
        dispatch(
          'app/addTempMessage',
          { message, timeout: 3000 },
          { root: true }
        )
        throw new Error('Valuation not available')
      })
      .finally(() => commit('app/setLoading', false, { root: true }))
  },
  async fetchAdvertisingData({ commit, dispatch }, valuationId: string) {
    commit('resetAdvertisingData')
    return client({
      url: `/v1/valuation/advertisingTransactions/${valuationId}`,
      method: 'GET',
    })
      .then(response => {
        commit('setAdvertisingData', response.data)
      })
      .catch(err => {
        const message = createMessage(
          'Valuace nemá k dispozici volné nabídky z okolí.',
          MessageType.Warning
        )
        dispatch(
          'app/addTempMessage',
          { message, timeout: 3000 },
          { root: true }
        )
      })
  },
  async fetchOffersData({ commit, dispatch }, valuationId: string) {
    commit('resetOffersData')
    return client({
      url: `/v1/valuation/offers/${valuationId}`,
      method: 'GET',
    })
      .then(response => {
        commit('setOffersData', response.data)
      })
      .catch(err => {
        const message = createMessage(
          'Valuace nemá k dispozici informace o stavu nabídek na trhu.',
          MessageType.Warning
        )
        dispatch(
          'app/addTempMessage',
          { message, timeout: 3000 },
          { root: true }
        )
      })
  },
  async recalculateValuation({ commit, dispatch, state }, valuationId: string) {
    commit('app/setLoading', true, { root: true })
    return client({
      url: `/v1/valuation/session/${valuationId}`,
      method: 'PATCH',
      data: JSON.stringify(state.edits),
    })
      .then(response => {
        commit('setEditObject', {})
        commit('setData', response.data)
      })
      .catch(err => {
        const message = createMessage(
          'Valuaci se nezdařilo přepočítat.',
          MessageType.Error
        )
        dispatch(
          'app/addTempMessage',
          { message, timeout: 3000 },
          { root: true }
        )
      })
      .finally(() => commit('app/setLoading', false, { root: true }))
  },
  async downloadPdf({ commit, dispatch, state }, valuationId: string) {
    commit('app/setLoading', true, { root: true })
    return client({
      url: `/v1/valuation/download/${valuationId}`,
      method: 'GET',
    })
      .then(response => {
        download(
          `data:application/pdf;base64,${response}`,
          `${valuationId}.pdf`,
          'application/pdf'
        )
        const message = createMessage('PDF stáhnuto', MessageType.Info)
        dispatch('app/addTempMessage', { message }, { root: true })
        if (router.currentRoute.name !== 'history') {
          router.push({
            name: 'history',
          })
        }
      })
      .catch(err => {
        const message = createMessage(
          'PDF se nepodařilo stáhnout.',
          MessageType.Error
        )
        dispatch(
          'app/addTempMessage',
          { message, timeout: 3000 },
          { root: true }
        )
      })
      .finally(() => commit('app/setLoading', false, { root: true }))
  },
}

const getters: GetterTree<ValuationEditState, RootState> = {
  edits: state => state.edits,
  data: state => state.data,
  advertisingData: state => state.advertisingData,
  offersData: state => state.offersData,
  areaType: state => getNestedValue(state.data, ['areaType'], ''),
  buildingType: state =>
    getNestedValue(state.data, ['info', 'buildingType'], BuildingType.New),
  buildingStatus: state =>
    getNestedValue(
      state.data,
      ['info', 'buildingStatus'],
      BuildingStatus.Finished
    ),
  layout: state => getNestedValue(state.data, ['info', 'layout'], 6),
  monthsPeriod: state => getNestedValue(state.data, ['info', 'months'], 12),
  garagePrice: state =>
    getNestedValue(state.data, ['info', 'projectGaragePrice']) || 'N/A',
  civilCode: state => getNestedValue(state.data, ['info', 'civilCode'], ''),
  coefficients: (state): { [coeffKey: string]: boolean } =>
    getNestedValue(state.data, ['coefficients'], {}),
  advertisingTransactions: state =>
    getNestedValue(state, ['advertisingData', 'transactions'], []),
  advertisingSummary: state =>
    getNestedValue(state, ['advertisingData', 'summary'], {}),
  configurationCode: state =>
    getNestedValue(state.data, ['configurationCode'], null),
}

const valuationEdit: Module<ValuationEditState, RootState> = {
  namespaced: true,
  state: {
    data: {},
    edits: {},
    advertisingData: {},
    offersData: {},
  },
  mutations,
  actions,
  getters,
}

export default valuationEdit
